import { type ModalWidth } from "components/modal";
import React, { useRef } from "react";
import { Modal } from "components";
import { ButtonSecondary } from "components/Atoms/Button";
import { type RemovableProps } from "components/Removables/Removable";
import { ModalPickerProps } from "./props";
import { useValidateIsNotNull } from "formvalidation/hooks";
import { SelectOrNew } from "./SelectOrNew";

interface Props<T> extends ModalPickerProps<T> {
  buttonLabel: string
  modalTitle: string
  modalSize: ModalWidth
  modalHeight?: number

  onNewClicked: () => void
  onModalClose?: () => void

  renderModalContent: (closeModal: () => void, onChange: (newValue: T) => void) => JSX.Element
  renderRemovable: (props: RemovableProps<T>) => JSX.Element
}

const ModalPickerWithChooseButton = <T extends unknown>(props: Props<T>) => {
  const { canCreateNew = false } = props;
  const button = props.button;

  let startOpen = false;

  if (button == null) {
    if (props.inPageEditing != null) {
      startOpen = props.inPageEditing.hasValue;
    } else {
      startOpen = props.nonInPageEditing?.startOpen ?? false;
    }
  }

  const value = props.nonInPageEditing?.value ?? undefined as unknown as T;
  const required = props.nonInPageEditing?.required ?? false;
  const validatorKey = props.nonInPageEditing?.validatorKey ?? undefined;
  const legacyHasError = props.nonInPageEditing?.legacyHasError ?? undefined;

  const [isModalOpen, setIsModalOpen] = React.useState(startOpen);
  const [modalJustClosed, setModalJustClosed] = React.useState(false);

  const onChange = (newValue: T) => {
    button?.onChange(newValue);
    props.nonInPageEditing?.onChange(newValue);
    props.inPageEditing?.onChange(newValue, true);

    if (newValue == null && required) {
      setIsModalOpen(true);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalJustClosed(true);
    setTimeout(() => {
      setModalJustClosed(false);
    }, 2500);
    props.onModalClose?.();
  };

  const cancel = () => {
    props.inPageEditing?.cancel();
    closeModal();
  }

  const hasValue = Boolean(value);

  const ref = useRef<HTMLDivElement>(null);

  let hasError: boolean = false;

  if (legacyHasError != null) {
    hasError = legacyHasError;
  }
  else if (required && legacyHasError == null) {
    const validation = useValidateIsNotNull({
      key: validatorKey,
      title: validatorKey,
      value: value,
      ref
    });

    hasError = validation.error.hasError;
  }

  return <>
    <div ref={ref}></div>
    {button != null && <ButtonSecondary
      label={button.label}
      loading={button.isLoading}
      disabled={button.isLoading}
      onClick={() => { setIsModalOpen(true); }}
    />}
    {button == null && !hasValue && <div className="flex">
      <SelectOrNew
        canCreateNew={canCreateNew}
        newButton={{
          label: RESX.Buttons.btnNew,
          onClick: props.onNewClicked
        }}
        selectButton={{
          label: props.buttonLabel,
          onClick: () => { setIsModalOpen(true); }
        }}
        hasError={hasError}
      />
    </div>}

    {button == null && hasValue && props.renderRemovable({ autoFocus: modalJustClosed, value: value, onChange, readonly: false, error: hasError })}

    <Modal
      isOpen={isModalOpen}
      handleClose={cancel}
      width={props.modalSize}
      gap="regular"
      title={props.modalTitle}
      height={props.modalHeight ?? 500}
    >
      {props.renderModalContent(closeModal, onChange)}
    </Modal>
  </>;
};

export default ModalPickerWithChooseButton;
